// VERA Field: everything that happens out in the field, tied to the records. Where you are and the time, the job you're
// at (from your device's location and each job's place), whether you're online (photos and notes wait on the device
// when there's no signal and go when it's back), the glasses, the job's digital twin (everything about it in one view:
// the record, what's missing, its photos and videos, notes, inspections), and every photo and video from the field with
// the job it's filed on. Archive keeps things out of the way without losing them; delete is only for what isn't filed
// anywhere. Talk to VERA or type to her about the job or the day, beside it.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Head, Icon, Orb, useStudio } from "../components/ui";
import { RecordsVera } from "../components/RecordsVera";
import { VoiceMode } from "../components/VoiceMode";
import { T, Tf, currentLang, dateLocale } from "../lib/i18n";
import { cpFetch } from "../lib/controlPlane";
import { config } from "../config/env";
import { CONTROL_PLANE_WORKSPACE, type ScopeId } from "../config/model";
import { vera } from "../api/vera";
import { CLOSED, crm, show, titleOf, type CrmHome, type CrmRecord, type CrmType } from "../lib/crm";
import { currentLocation, locationOn, setLocationOn, type Here } from "../lib/location";
import { distanceText, jobPlaces, type JobPlace } from "../lib/fieldPlace";
import { indexedDbStore, notesField, photosField } from "../lib/fieldQueue";
import { missing } from "../lib/completeness";

type Media = { id: string; workspace: string; takenBy: string; note: string; takenAt: string; size: number; kind?: "photo" | "video"; archived?: boolean; lat?: number | null; lon?: number | null };
type Filed = { type: CrmType; record: CrmRecord };

const jobsOf = (types: CrmType[]) => types.find((x) => x.id === "jobs") ?? types.find((x) => x.id === "shifts") ?? types.find((x) => x.statusField && x.fields.some((f) => f.kind === "date"));
const when = (iso: string | number) => new Date(iso).toLocaleString(dateLocale(currentLang()), { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });

/** A picture as a JPEG data URL (the field store takes JPEG), at most 2400 px on its long side. */
async function asJpeg(file: File): Promise<string> {
  const url = URL.createObjectURL(file);
  try {
    const img = new Image(); img.src = url; await img.decode();
    const k = Math.min(1, 2400 / Math.max(img.naturalWidth, img.naturalHeight));
    const c = document.createElement("canvas"); c.width = Math.round(img.naturalWidth * k); c.height = Math.round(img.naturalHeight * k);
    c.getContext("2d")!.drawImage(img, 0, 0, c.width, c.height);
    return c.toDataURL("image/jpeg", 0.86);
  } finally { URL.revokeObjectURL(url); }
}

export function VeraField() {
  const { getToken, profile, go, toast, consent, openConsent } = useStudio();
  const live = vera.mode === "live";
  const scope = (profile.scopes.find((s) => CONTROL_PLANE_WORKSPACE[s]) ?? profile.scopes[0]) as ScopeId | undefined;
  const workspaceId = scope ? CONTROL_PLANE_WORKSPACE[scope]?.workspaceId ?? "" : "";
  const [home, setHome] = useState<CrmHome | null>(null);
  const [lists, setLists] = useState<Record<string, CrmRecord[]>>({});
  const [media, setMedia] = useState<Media[] | null>(null);
  const [thumbs, setThumbs] = useState<Record<string, string>>({});
  const [here, setHere] = useState<Here | null>(null);
  const [places, setPlaces] = useState<JobPlace[]>([]);
  const [chosen, setChosen] = useState<string>("");
  const [online, setOnline] = useState(typeof navigator === "undefined" ? true : navigator.onLine);
  const [waiting, setWaiting] = useState(0);
  const [tab, setTab] = useState<"photo" | "video" | "archived">("photo");
  const [veraOpen, setVeraOpen] = useState<{ start?: string } | null>(null);
  const [voice, setVoice] = useState(false);
  const [busy, setBusy] = useState(false);
  const pick = useRef<HTMLInputElement>(null);
  const [now, setNow] = useState(() => new Date());
  useEffect(() => { const t = window.setInterval(() => setNow(new Date()), 30_000); return () => window.clearInterval(t); }, []);

  const loadRecords = useCallback(async () => {
    try {
      const h = await crm.home(getToken); setHome(h);
      const next: Record<string, CrmRecord[]> = {};
      await Promise.all(h.schema.types.map(async (t) => { try { next[t.id] = await crm.list(getToken, t.id); } catch { next[t.id] = []; } }));
      setLists(next);
    } catch { setHome(null); }
  }, [getToken]);
  const loadMedia = useCallback(async () => {
    if (!workspaceId) return;
    try { setMedia((await cpFetch<{ photos: Media[] }>(getToken, `/v1/field/photos?workspaceId=${encodeURIComponent(workspaceId)}`)).photos); }
    catch { setMedia([]); }
  }, [getToken, workspaceId]);
  useEffect(() => { if (live) { void loadRecords(); void loadMedia(); } }, [live, loadRecords, loadMedia]);

  // Online or not, and what is waiting on this device to be sent.
  useEffect(() => {
    const on = () => setOnline(navigator.onLine);
    window.addEventListener("online", on); window.addEventListener("offline", on);
    const count = () => void indexedDbStore()?.all().then((a) => setWaiting(a.length)).catch(() => undefined);
    count(); const t = window.setInterval(count, 15_000);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", on); window.clearInterval(t); };
  }, []);

  // Where the device is (only while Location is on), and the jobs by distance.
  useEffect(() => { if (locationOn()) void currentLocation().then((h) => setHere(h ?? null)); }, []);
  const types = home?.schema.types ?? [];
  const jobsType = jobsOf(types);
  const open = useMemo(() => (jobsType ? (lists[jobsType.id] ?? []).filter((j) => !jobsType.statusField || !CLOSED.test(String(j.values[jobsType.statusField] ?? ""))) : []), [jobsType, lists]);
  useEffect(() => { if (jobsType && open.length) void jobPlaces(open, jobsType, types, lists, here).then(setPlaces); }, [jobsType, open, types, lists, here]);
  const nearest = places.find((p) => p.metres !== null && p.metres < 400);
  const job = (chosen && open.find((j) => j.id === chosen)) || nearest?.job || null;

  // Which record each photo or video is filed on (the records' photo fields hold their ids).
  const filed = useMemo(() => {
    const m: Record<string, Filed> = {};
    for (const t of types) for (const f of t.fields.filter((x) => x.kind === "photos")) for (const r of lists[t.id] ?? []) {
      const ids = r.values[f.id];
      if (Array.isArray(ids)) for (const id of ids) if (typeof id === "string") m[id] ??= { type: t, record: r };
    }
    return m;
  }, [types, lists]);

  // A second upload of the same picture a moment later is marked, so nothing ends up twice.
  const copies = useMemo(() => {
    const set = new Set<string>();
    const all = [...(media ?? [])].sort((a, b) => +new Date(a.takenAt) - +new Date(b.takenAt));
    all.forEach((m, i) => { if (all.slice(0, i).some((x) => x.size === m.size && x.kind === m.kind && Math.abs(+new Date(x.takenAt) - +new Date(m.takenAt)) < 180_000)) set.add(m.id); });
    return set;
  }, [media]);

  const shown = (media ?? []).filter((m) => (tab === "archived" ? m.archived : !m.archived && (m.kind ?? "photo") === tab)).slice(0, 48);
  useEffect(() => {
    let alive = true;
    void (async () => {
      for (const m of shown) {
        if (!alive || thumbs[m.id] || m.kind === "video") continue;
        try {
          const res = await fetch(`${config.controlPlaneUrl}/v1/field/photo?id=${encodeURIComponent(m.id)}`, { headers: { Authorization: `Bearer ${await getToken()}` } });
          if (res.ok && alive) { const u = URL.createObjectURL(await res.blob()); setThumbs((x) => ({ ...x, [m.id]: u })); }
        } catch { /* stays blank */ }
      }
    })();
    return () => { alive = false; };
  }, [shown.map((m) => m.id).join(","), getToken]); // eslint-disable-line react-hooks/exhaustive-deps

  async function fileOn(m: Media, jobId: string) {
    const r = jobsType ? (lists[jobsType.id] ?? []).find((j) => j.id === jobId) : undefined;
    const pf = photosField(jobsType);
    if (!r || !pf || !jobsType) { toast(T("That job has no place for photos yet. Ask VERA to add one.")); return; }
    const before = Array.isArray(r.values[pf.id]) ? (r.values[pf.id] as string[]) : [];
    try { await crm.update(getToken, jobsType.id, r.id, r.version, { [pf.id]: [...before, m.id] }); toast(Tf("Filed on {job}.", { job: titleOf(jobsType, r) })); await loadRecords(); }
    catch (e) { toast(e instanceof Error ? e.message : T("That didn't work. Please try again.")); }
  }
  async function archive(m: Media, archived: boolean) {
    try { await cpFetch(getToken, "/v1/field/photo/archive", { method: "POST", body: JSON.stringify({ id: m.id, archived }) }); setMedia((x) => (x ?? []).map((y) => (y.id === m.id ? { ...y, archived } : y))); toast(archived ? T("Archived. It's under Archived if you need it.") : T("Back in VERA Field.")); }
    catch (e) { toast(e instanceof Error ? e.message : T("That didn't work. Please try again.")); }
  }
  async function remove(m: Media) {
    if (!window.confirm(T("Delete this for good? It isn't filed on any record. Archive keeps it instead."))) return;
    try { await cpFetch(getToken, `/v1/field/photo?id=${encodeURIComponent(m.id)}`, { method: "DELETE" }); setMedia((x) => (x ?? []).filter((y) => y.id !== m.id)); toast(T("Deleted.")); }
    catch (e) { toast(e instanceof Error ? e.message : T("That didn't work. Please try again.")); }
  }

  // Add pictures or videos from this device: saved to the field store with where they were taken, filed on the job.
  async function add(files: FileList) {
    if (!workspaceId) return;
    setBusy(true);
    try {
      for (const f of Array.from(files)) {
        let id: string | undefined;
        if (f.type.startsWith("image/")) {
          const r = await cpFetch<{ id: string }>(getToken, "/v1/field/photos", { method: "POST", body: JSON.stringify({ workspaceId, dataUrl: await asJpeg(f), note: f.name, lat: here?.lat, lon: here?.lon }) });
          id = r.id;
        } else if (/^video\/(mp4|quicktime)$/.test(f.type)) {
          const q = new URLSearchParams({ workspaceId, note: f.name, ...(here ? { lat: String(here.lat), lon: String(here.lon) } : {}) });
          const res = await fetch(`${config.controlPlaneUrl}/v1/field/videos?${q}`, { method: "POST", headers: { Authorization: `Bearer ${await getToken()}`, "Content-Type": f.type }, body: f });
          if (!res.ok) throw new Error(((await res.json().catch(() => ({}))) as { error?: string }).error ?? T("The video couldn't be saved."));
          id = ((await res.json()) as { id: string }).id;
        } else { toast(Tf("{name}: only pictures and MP4 or MOV videos.", { name: f.name })); continue; }
        if (id && job) await fileOn({ id } as Media, job.id);
      }
      await loadMedia();
    } catch (e) { toast(e instanceof Error ? e.message : T("That didn't work. Please try again.")); }
    finally { setBusy(false); }
  }

  async function useLocation() {
    if (!(await setLocationOn(true))) { toast(T("Your browser didn't allow the location. You can allow it in the browser's site settings.")); return; }
    setHere((await currentLocation()) ?? null);
  }
  const talk = () => { if (consent.mic) setVoice(true); else { toast(T("Turn on the microphone for voice in Privacy & consent first.")); openConsent(); } };

  if (!live) return <><Head title={T("VERA Field")} /><p className="mute">{T("VERA Field works with live VERA.")}</p></>;
  const today = (media ?? []).filter((m) => new Date(m.takenAt).toDateString() === now.toDateString());
  const lastGlasses = (media ?? []).find((m) => /glass/i.test(m.note));
  const jobFiled = job ? (media ?? []).filter((m) => filed[m.id]?.record.id === job.id) : [];

  return (
    <>
      <Head title={T("VERA Field")} lead={T("Everything from the field, tied to your records: where you are, the job you're at, photos, videos, notes and inspections. VERA files each one on the right job.")}>
        <button className="btn rec-talk" onClick={talk}><Icon name="mic" size={15} /> {T("Talk to VERA")}</button>
        <button className={`btn sm${veraOpen ? " on" : ""}`} onClick={() => setVeraOpen(veraOpen ? null : {})} aria-pressed={Boolean(veraOpen)}><Icon name="chat" size={14} /> {T("Type")}</button>
      </Head>
      {voice && scope && <VoiceMode scope={scope} getToken={getToken} onClose={() => { setVoice(false); void loadRecords(); void loadMedia(); }} />}

      <div className="field-strip">
        <div className="card field-tile">
          <span className="faint small"><Icon name="globe" size={13} /> {T("Location and time")}</span>
          <b className="num">{now.toLocaleTimeString(dateLocale(currentLang()), { hour: "2-digit", minute: "2-digit" })}</b>
          {here ? <span className="small">{nearest ? Tf("At {job}", { job: jobsType ? titleOf(jobsType, nearest.job) : "" }) : places[0]?.metres != null && jobsType ? Tf("Nearest job: {job}, {d}", { job: titleOf(jobsType, places[0].job), d: T(distanceText(places[0].metres!)) }) : T("Location on")}</span>
            : <button className="btn sm" onClick={() => void useLocation()}>{T("Turn on location")}</button>}
        </div>
        <div className={`card field-tile${online ? "" : " warn"}`}>
          <span className="faint small"><Icon name="wave" size={13} /> {T("Connection")}</span>
          <b>{online ? T("Online") : T("Offline")}</b>
          <span className="small">{waiting ? Tf("{n} waiting on this device", { n: String(waiting) }) : online ? T("Everything is sent") : T("Photos and notes wait here and go when the signal is back")}</span>
        </div>
        <div className="card field-tile">
          <span className="faint small"><Icon name="img" size={13} /> {T("Meta glasses")}</span>
          <b>{lastGlasses ? Tf("Last look {when}", { when: when(lastGlasses.takenAt) }) : T("Not seen yet")}</b>
          <span className="small">{T("Connect them in the VERA Field app on your phone")}</span>
        </div>
        <div className="card field-tile">
          <span className="faint small"><Icon name="cal" size={13} /> {T("Today in the field")}</span>
          <b className="num">{today.filter((m) => m.kind !== "video").length} {T("photos")} · {today.filter((m) => m.kind === "video").length} {T("videos")}</b>
          <button className="btn sm" onClick={() => setVeraOpen({ start: T("What happened in the field today? Sum up the photos, videos, notes and jobs, and what still needs doing.") })}>{T("Sum up the day")}</button>
        </div>
      </div>

      {jobsType && (
        <section className="card twin">
          <div className="row between" style={{ gap: 8, flexWrap: "wrap" }}>
            <div className="row" style={{ gap: 8 }}><Orb size={16} /><h2>{T("Digital twin")}</h2><span className="faint small">{T("everything about the job, in one place")}</span></div>
            <select value={job?.id ?? ""} onChange={(e) => setChosen(e.target.value)} aria-label={T("Which job")} style={{ padding: "7px 10px", borderRadius: 10 }}>
              <option value="">{nearest ? T("The job you're at") : Tf("Choose a {kind}", { kind: jobsType.name.toLowerCase() })}</option>
              {open.map((j) => <option key={j.id} value={j.id}>{titleOf(jobsType, j)}</option>)}
            </select>
          </div>
          {!job ? <p className="faint" style={{ margin: "8px 0 0" }}>{here ? T("You're not at any open job right now. Choose one to see everything about it.") : T("Turn on location and VERA opens the job you're at, or choose one.")}</p> : (() => {
            const gaps = missing(jobsType, job);
            const nf = notesField(jobsType);
            const notes = nf ? String(job.values[nf.id] ?? "") : "";
            const titles: Record<string, string> = {};
            for (const t of types) for (const r of lists[t.id] ?? []) titles[r.id] = titleOf(t, r);
            const linkIds = new Set([job.id, ...jobsType.fields.filter((f) => f.kind === "link").map((f) => String(job.values[f.id] ?? ""))]);
            const inspType = types.find((t) => /inspect/i.test(t.id) || /inspect/i.test(t.plural));
            const inspections = inspType ? (lists[inspType.id] ?? []).filter((r) => inspType.fields.some((f) => f.kind === "link" && linkIds.has(String(r.values[f.id] ?? "")))) : [];
            return (
              <div className="twin-body">
                <div className="stack" style={{ gap: 6 }}>
                  <h3 style={{ margin: 0 }}>{titleOf(jobsType, job)}</h3>
                  <div className="kv">{jobsType.fields.filter((f) => !f.hidden && f.id !== jobsType.titleField && !["longtext", "photos", "links"].includes(f.kind)).slice(0, 6).map((f) => (
                    <span key={f.id} style={{ display: "contents" }}><b>{f.name}</b><span>{show(f, job.values[f.id], titles, home?.people ?? []) || <span className="rec-empty">{T("missing")}</span>}</span></span>))}</div>
                  {gaps.length > 0 && <span className="small" style={{ color: "#ffc861" }}>{Tf("{n} details missing", { n: String(gaps.length) })}: {gaps.map((f) => f.name).join(", ")}</span>}
                  <div className="row" style={{ gap: 6, flexWrap: "wrap" }}>
                    <button className="btn sm" onClick={() => go("records", { crmType: jobsType.id, crmRecord: job.id })}>{T("Open in Records")}</button>
                    <button className="btn sm primary" onClick={() => setVeraOpen({ start: T("Tell me where this job stands and what's still needed.") })}><Icon name="vera" size={13} /> {T("Ask VERA about this job")}</button>
                    <button className="btn sm" disabled={busy} onClick={() => pick.current?.click()}><Icon name="plus" size={13} /> {busy ? T("Saving…") : T("Add photos or videos")}</button>
                  </div>
                </div>
                <div className="stack" style={{ gap: 6 }}>
                  <b className="small">{T("Notes")}</b>
                  {notes ? <><p className="pre small" style={{ margin: 0, maxHeight: 120, overflow: "auto" }}>{notes}</p><button className="linkbtn small" style={{ alignSelf: "flex-start" }} onClick={() => setVeraOpen({ start: T("Summarise the notes on this job in a few lines: what was done, what was found, what's next.") })}>{T("Summarise with VERA")}</button></> : <span className="faint small">{T("No notes yet. Tell VERA, and she writes them on the job.")}</span>}
                  <b className="small" style={{ marginTop: 6 }}>{T("Inspections")}</b>
                  {inspType ? (inspections.length ? inspections.slice(0, 5).map((r) => <button key={r.id} className="linkbtn small" style={{ textAlign: "left" }} onClick={() => go("records", { crmType: inspType.id, crmRecord: r.id })}>{titleOf(inspType, r)}</button>)
                    : <span className="faint small">{T("None for this job yet.")}</span>)
                    : <button className="linkbtn small" style={{ alignSelf: "flex-start" }} onClick={() => setVeraOpen({ start: T("Set up inspections for my jobs: a list linked to jobs and boats, with what was checked, the result and photos.") })}>{T("Set up inspections with VERA")}</button>}
                  <b className="small" style={{ marginTop: 6 }}>{T("From the field")}</b>
                  <span className="faint small">{jobFiled.length ? Tf("{n} photos and videos filed on this job", { n: String(jobFiled.length) }) : T("Nothing filed on this job yet.")}</span>
                </div>
              </div>);
          })()}
        </section>)}
      <input ref={pick} type="file" hidden multiple accept="image/*,video/mp4,video/quicktime" onChange={(e) => { if (e.target.files) void add(e.target.files); e.target.value = ""; }} />

      <section className="card">
        <div className="row between" style={{ gap: 8, flexWrap: "wrap" }}>
          <div className="tabs" role="tablist" style={{ margin: 0 }}>
            {(["photo", "video", "archived"] as const).map((k) => <button key={k} role="tab" aria-selected={tab === k} className={tab === k ? "on" : ""} onClick={() => setTab(k)}>{k === "photo" ? T("Photos") : k === "video" ? T("Videos") : T("Archived")} <span className="faint small">{(media ?? []).filter((m) => (k === "archived" ? m.archived : !m.archived && (m.kind ?? "photo") === k)).length}</span></button>)}
          </div>
          <button className="btn sm" disabled={busy} onClick={() => pick.current?.click()}><Icon name="plus" size={13} /> {T("Add photos or videos")}</button>
        </div>
        {media === null ? <p className="faint">{T("Loading…")}</p> : !shown.length ? <p className="faint">{tab === "archived" ? T("Nothing archived.") : T("Nothing here yet. Photos and videos from the glasses and phones appear here.")}</p> : (
          <div className="field-grid">{shown.map((m) => {
            const on = filed[m.id];
            return (
              <div key={m.id} className="field-item">
                {m.kind === "video" ? <div className="field-video"><Icon name="img" size={22} /><span className="small">{T("Video")}</span></div> : thumbs[m.id] ? <img src={thumbs[m.id]} alt={m.note || T("Photo from the field")} /> : <div className="thumb-empty" />}
                <div className="small"><b>{m.takenBy || T("Someone")}</b> · <span className="faint">{when(m.takenAt)}</span>{m.lat != null && <span className="faint"> · {T("located")}</span>}</div>
                {m.note && <div className="faint small">{m.note.slice(0, 80)}</div>}
                {copies.has(m.id) && <span className="tag" style={{ alignSelf: "flex-start" }}>{T("Looks like a copy")}</span>}
                {on ? <button className="linkbtn small" style={{ textAlign: "left" }} onClick={() => go("records", { crmType: on.type.id, crmRecord: on.record.id })}>{T("On")} {on.type.name.toLowerCase()}: {titleOf(on.type, on.record)}</button>
                  : jobsType && <select value="" onChange={(e) => { if (e.target.value) void fileOn(m, e.target.value); }} aria-label={T("File on a job")} style={{ fontSize: 12.5, padding: "4px 6px", borderRadius: 8 }}>
                    <option value="">{T("File on a job…")}</option>
                    {job && <option value={job.id}>{T("This job:")} {titleOf(jobsType, job)}</option>}
                    {open.filter((j) => j.id !== job?.id).map((j) => <option key={j.id} value={j.id}>{titleOf(jobsType, j)}</option>)}
                  </select>}
                <div className="row" style={{ gap: 8 }}>
                  <button className="linkbtn small" onClick={() => void archive(m, !m.archived)}>{m.archived ? T("Restore") : T("Archive")}</button>
                  {!on && <button className="linkbtn small" onClick={() => void remove(m)}>{T("Delete")}</button>}
                </div>
              </div>);
          })}</div>)}
      </section>

      {veraOpen && jobsType && (
        <RecordsVera type={jobsType} record={job} title={job ? titleOf(jobsType, job) : undefined} owner={home?.me.role === "owner"} start={veraOpen.start ?? null}
          onChanged={() => { void loadRecords(); void loadMedia(); }} onClose={() => setVeraOpen(null)} />)}
    </>
  );
}
